import { useId, type ReactNode } from 'react';
import { useUiLabels } from '../labels';
import { cn } from '../lib/cn';
import { FieldDescription, FieldError } from './field-message';

/** Props a control needs to be wired to its label, help text and error. */
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  required?: boolean;
}

export interface FormFieldProps {
  label: ReactNode;
  description?: ReactNode;
  /** Error text in words, shown below the field (never colour only). */
  error?: ReactNode;
  required?: boolean;
  /** Marks the field as optional in the label, for forms where most fields are required. */
  optional?: boolean;
  className?: string;
  id?: string;
  children: (control: FieldControlProps) => ReactNode;
}

/**
 * Label, control, help text and error in the standard layout. The control is passed as a render
 * function, so ids and aria attributes are always connected:
 *
 * ```tsx
 * <FormField label={t('…')} error={errors.name?.message}>
 *   {(field) => <Input {...field} {...register('name')} />}
 * </FormField>
 * ```
 */
export function FormField({
  label,
  description,
  error,
  required,
  optional,
  className,
  id,
  children,
}: FormFieldProps) {
  const labels = useUiLabels();
  const generated = useId();
  const controlId = id ?? generated;
  const descriptionId = description ? `${controlId}-description` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={controlId} className="flex items-baseline gap-1.5 font-medium text-fg">
        {label}
        {required && (
          <span aria-hidden className="text-danger">
            *
          </span>
        )}
        {optional && !required && (
          <span className="text-xs font-normal text-fg-subtle">{labels.optional}</span>
        )}
      </label>
      {children({
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required: required || undefined,
      })}
      {description && <FieldDescription id={descriptionId}>{description}</FieldDescription>}
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </div>
  );
}
